import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AppLayout } from '@/components/layout/AppLayout';
import { PageHeader } from '@/components/layout/PageHeader';
import { AthleteProfileSection } from '@/components/settings/AthleteProfileSection';
import { TrainingPreferencesSection } from '@/components/settings/TrainingPreferencesSection';
import { ProfileAvatar } from '@/components/profile/ProfileAvatar';
import { PaceZonesHud } from '@/components/profile/PaceZonesHud';
import { PowerZonesHud } from '@/components/profile/PowerZonesHud';
import { SystemCalibrationCard } from '@/components/settings/SystemCalibrationCard';
import { IntegrationsSection } from '@/components/settings/IntegrationsSection';
import { useAuth } from '@/context/AuthContext';
import { useAthleteProfile } from '@/hooks/useAthleteProfile';
import { fetchTrainingPreferences } from '@/lib/api';
import { Shield, Activity, Zap } from 'lucide-react';

/** Map raw sport enum values to display names. */
const SPORT_DISPLAY_NAMES: Record<string, string> = {
  run: 'Running',
  bike: 'Cycling',
  swim: 'Swimming',
  tri: 'Triathlon',
};

function sportDisplayName(raw: string | undefined): string {
  if (!raw || raw === 'unknown') return '—';
  return SPORT_DISPLAY_NAMES[raw] ?? raw;
}

/** Map detected experience level to badge label + color. */
const EXP_RANK: Record<string, { label: string; color: string }> = {
  elite: { label: 'ELITE', color: '#00FFC8' },
  competitive: { label: 'COMPETITIVE', color: '#00FFC8' },
  advanced: { label: 'ADVANCED', color: 'hsl(170, 70%, 50%)' },
  structured: { label: 'STRUCTURED', color: 'hsl(170, 70%, 50%)' },
  intermediate: { label: 'INTERMEDIATE', color: 'hsl(50, 80%, 55%)' },
  novice: { label: 'NOVICE', color: 'hsl(35, 85%, 55%)' },
  beginner: { label: 'BEGINNER', color: '#A0A0A0' },
};

const EXP_FALLBACK = { label: 'ROOKIE', color: '#A0A0A0' } as const;

export default function Profile() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { profile } = useAthleteProfile();
  const location = useLocation();

  // Scroll to hash target (e.g. /profile#experience)
  useEffect(() => {
    if (location.hash) {
      // Small delay to let sections render
      const timer = setTimeout(() => {
        const el = document.querySelector(location.hash);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Brief highlight flash
          el.classList.add('ring-1', 'ring-[#00FFC8]/40', 'rounded-lg');
          setTimeout(() => el.classList.remove('ring-1', 'ring-[#00FFC8]/40', 'rounded-lg'), 2000);
        }
      }, 400);
      return () => clearTimeout(timer);
    }
  }, [location.hash]);

  // Load FTP + primary_sports for zone huds
  const [ftpWatts, setFtpWatts] = useState<number | null>(null);
  const [primarySports, setPrimarySports] = useState<string[]>([]);
  useEffect(() => {
    fetchTrainingPreferences()
      .then((prefs) => {
        setFtpWatts(prefs.ftp_watts ?? null);
        setPrimarySports(prefs.primary_sports ?? []);
      })
      .catch(() => { /* non-critical — silently ignore */ });
  }, []);

  const isCyclist = primarySports.some(s => s === 'cycling' || s === 'bike');
  const isRunner = primarySports.some(s => s === 'running' || s === 'run')
    || (!isCyclist && primarySports.length === 0); // default to runner view if no prefs yet
  
  const firstName = profile?.identity?.first_name;
  const lastName = profile?.identity?.last_name;
  const fullName = [firstName, lastName].filter(Boolean).join(' ');
  const displayName = fullName || user?.email?.split('@')[0] || 'Athlete';

  // Derive rank from detected experience level (top-level from API, fallback to training_context)
  const experienceLevel = profile?.experience_level ?? profile?.training_context?.experience_level;
  const rank = (experienceLevel && EXP_RANK[experienceLevel.toLowerCase()]) || EXP_FALLBACK;

  return (
    <AppLayout>
      <div className="space-y-5">
        <PageHeader
          title={t('profile.title')}
          subtitle={t('profile.subtitle')}
          statusLabel={t('profile.statusLabel')}
          status="green"
        />

        {/* Desktop: Sidebar + 2-Column Grid / Mobile: Stacked */}
        <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-5">
          
          {/* LEFT SIDEBAR — Profile Card */}
          <div className="space-y-4">
            {/* Identity HUD */}
            <div className="rounded-lg border border-white/[0.08] bg-[rgba(12,16,24,0.85)] backdrop-blur-[16px] p-5 shadow-[inset_0_1px_0_rgba(0,255,200,0.04)]">
              <div className="flex flex-col items-center text-center">
                <ProfileAvatar 
                  name={fullName} 
                  email={user?.email} 
                  size="xl" 
                />
                <h2 className="mt-4 text-lg font-semibold text-foreground tracking-wide">
                  {displayName}
                </h2>
                <p className="text-[10px] font-mono text-muted-foreground tracking-wider mt-1">
                  {user?.email || '—'}
                </p>

                {/* Athlete Rank Badge */}
                <div className="mt-4 flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/[0.08] bg-white/[0.02]">
                  <Shield className="h-3 w-3" style={{ color: rank.color }} />
                  <span
                    className="text-[10px] font-mono font-bold tracking-[0.15em]"
                    style={{ color: rank.color }}
                  >
                    {rank.label}
                  </span>
                </div>
              </div>

              {/* Quick Stats */}
              <div className="mt-5 pt-4 border-t border-white/[0.06] grid grid-cols-2 gap-3">
                <div className="text-center">
                  <div className="flex items-center justify-center gap-1 mb-1">
                    <Activity className="h-3 w-3 text-[#00FFC8]" />
                    <span className="text-[8px] font-mono tracking-wider text-white/40 uppercase">{t('profile.sportStat')}</span>
                  </div>
                  <span className="text-xs font-mono text-foreground">
                    {sportDisplayName(profile?.training_context?.primary_sport)}
                  </span>
                </div>
                <div className="text-center">
                  <div className="flex items-center justify-center gap-1 mb-1">
                    <Zap className="h-3 w-3 text-[hsl(35,85%,55%)]" />
                    <span className="text-[8px] font-mono tracking-wider text-white/40 uppercase">{t('profile.expStat')}</span>
                  </div>
                  <span className="text-xs font-mono text-foreground">
                    {rank.label !== 'ROOKIE' ? rank.label : '—'}
                  </span>
                </div>
              </div>
            </div>

            {/* Integrations HUD (desktop sidebar) */}
            <div className="hidden lg:block">
              <IntegrationsSection />
            </div>

            {/* Zone HUD (desktop sidebar only) — Pace for runners, Power for cyclists */}
            <div className="hidden lg:block">
              {isCyclist
                ? <PowerZonesHud ftpWatts={ftpWatts} />
                : <PaceZonesHud />
              }
            </div>
          </div>

          {/* RIGHT — Main Control Grid */}
          <div className="space-y-5">
            {/* System Calibration */}
            <SystemCalibrationCard />

            {/* Training Engine (merged) + Athlete Profile */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
              <TrainingPreferencesSection />
              <AthleteProfileSection />
            </div>

            {/* Mobile-only panels */}
            <div className="lg:hidden space-y-4">
              <IntegrationsSection />
              {isCyclist
                ? <PowerZonesHud ftpWatts={ftpWatts} />
                : <PaceZonesHud />
              }
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
